import React from 'react';
import { observable } from 'mobx';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { withRouter } from 'react-router';
import Helmet from 'helmet/helmet';
import { IntlMessage } from 'react-intl/intl-message';
import { Link } from 'react-router-dom';
import Styled from 'styled';

import LinkView from 'lib/ui/link-view';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import withDialog from 'lib/alerts/with-dialog';
import { DeleteDialog } from 'lib/alerts/dialog';

import { Site } from 'four-nets/site';
import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';
import newArticle from 'four-nets/icons/svg/icon_new-article.svg';
import notification from 'four-nets/icons/svg/icon_notifications.svg';
import ActionButtonView from 'four-nets/ui/action-button/action-button-view';
import TitleView from 'four-nets/ui/title-view';
import PagerView from 'four-nets/ui/pager-view';
import { CategoryStore } from 'four-nets/forum/forum';
import BigButtonView from 'four-nets/ui/big-button/big-button-view';
import NewThreadView, { NewThreadViewModel } from 'four-nets/forum/thread/new-thread/new-thread-view';
import { HideForVendor } from 'four-nets/vendor/permissions';

import PlaceholderThreadRow from '../threads-table/placeholder-thread-row';
import defaultStyles from './subscribed-topics-view.scss';

@Styled(defaultStyles)
@observer
class SubscribedTopicsView extends React.Component {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
        categoryStore: PropTypes.instanceOf(CategoryStore),
        authCheck: PropTypes.func,
    };

    constructor(props, context) {
        super(props);

        const { categoryStore, site } = context;
        categoryStore.site = site;

        this.newThreadViewModel = new NewThreadViewModel({
            categoryStore,
            userStore: categoryStore.userStore,
        });
    }

    newThreadViewModel;
    newThreadView;

    setNewThreadView = ref => {
        this.newThreadView = ref;
    };

    onNewThreadClick = () => {
        const { newThreadView } = this;

        if (newThreadView) {
            const action = () => {
                newThreadView.show({
                    form: this.newThreadViewModel.setupForm(),
                });
                return Promise.resolve();
            };

            this.context.authCheck(action, AuthOrigin.NewThread);
        }
    };

    render() {
        const { styles, categoriesTableViewModel, threadsTableViewModel } = this.props;
        const { newThreadViewModel } = this;

        return (
            <div>
                <div className={styles.title}>
                    <TitleView
                        style={{ components: styles.titleViewComponents }}
                        title={
                            <h1 className={styles.heading}>
                                <FormattedMessage id='SUBSCRIBED_THREADS'>
                                    {msg => <span className={styles.titleText}>{msg}</span>}
                                </FormattedMessage>
                            </h1>
                        }
                        leftComponent={
                            <HideForVendor>
                                <BigButtonView
                                    icon={newArticle}
                                    label={<FormattedMessage id='NEW_THREAD_BUTTON' />}
                                    onClick={this.onNewThreadClick}
                                />
                            </HideForVendor>
                        }
                    />
                </div>
                <SubscribedThreadsView threadsTableViewModel={threadsTableViewModel} />
                <div className={styles.title}>
                    <TitleView
                        title={
                            <h2 className={styles.heading}>
                                <FormattedMessage id='SUBSCRIBED_CATEGORIES'>
                                    {msg => <span className={styles.titleText}>{msg}</span>}
                                </FormattedMessage>
                            </h2>
                        }
                    />
                </div>
                <SubscribedCategoriesView categoriesTableViewModel={categoriesTableViewModel} />
                <NewThreadView innerRef={this.setNewThreadView} newThreadViewModel={newThreadViewModel} />
            </div>
        );
    }
}

export class CategoriesTableViewModel {
    @observable categories = [];

    setCategories(categories) {
        this.categories = categories;
    }

    toJSON() {
        return {
            categories: this.categories,
        };
    }
}

@Styled(defaultStyles)
@withDialog
@observer
class SubscribedCategoriesView extends React.Component {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    unsubscribe = category => {
        return new Promise((resolve, reject) => {
            this.props.showDialog(
                new DeleteDialog({
                    message: new IntlMessage({
                        message: defineMessages({ ARE_YOU_SURE: { id: 'ARE_YOU_SURE' } }).ARE_YOU_SURE,
                        values: { gender: 'm' },
                    }),
                    label: new IntlMessage({
                        message: defineMessages({ DISABLE_NOTIFICATIONS: { id: 'DISABLE_NOTIFICATIONS' } })
                            .DISABLE_NOTIFICATIONS,
                    }),
                    del: () => category.toggleNotifications().finally(resolve),
                    cancel: resolve,
                })
            );
        });
    };

    renderHelmet = H => {
        const { site } = this.context;
        return (
            <FormattedMessage id='FORUM_LINK_LABEL'>
                {linkLabel => (
                    <FormattedMessage id='SUBSCRIBED_THREADS'>
                        {subscribedThreads => {
                            const title = `${linkLabel} - ${subscribedThreads} | ${site.name}`;

                            return (
                                <H>
                                    <title>{title}</title>,
                                    <meta property='og:title' content={title} />
                                </H>
                            );
                        }}
                    </FormattedMessage>
                )}
            </FormattedMessage>
        );
    };

    render() {
        const { styles, categoriesTableViewModel } = this.props;

        return (
            <div className={styles.threadsTable}>
                <Helmet render={this.renderHelmet} />
                <table className={styles.table}>
                    <thead className={styles.heading}>
                        <tr className={styles.headingRow}>
                            <th className={styles.headingLabel}>
                                <FormattedMessage id='TITLE' />
                            </th>
                            <th />
                        </tr>
                    </thead>
                    <tbody className={styles.body}>
                        {!categoriesTableViewModel || categoriesTableViewModel.loading
                            ? Array(5)
                                  .fill(null)
                                  .map((_, index) => <PlaceholderThreadRow borderTop={index !== 0} />)
                            : null}
                        {categoriesTableViewModel?.categories &&
                            categoriesTableViewModel.categories
                                .filter(c => c.subscribed)
                                .map(category => (
                                    <tr key={category.id} className={styles.thread}>
                                        <td className={styles.title}>
                                            <Link to={`/forum/category/${category.slug}/`} className={styles.link}>
                                                {category.name}
                                            </Link>
                                        </td>
                                        <td>
                                            <ActionButtonView
                                                onClick={() => this.unsubscribe(category)}
                                                icon={<SVGIcon style={{ image: styles.icon }} src={notification} />}
                                            >
                                                <FormattedMessage id='DISABLE_NOTIFICATIONS' />
                                            </ActionButtonView>
                                        </td>
                                    </tr>
                                ))}
                    </tbody>
                </table>
            </div>
        );
    }
}

@Styled(defaultStyles)
@withDialog
@observer
class SubscribedThreadsView extends React.Component {
    @observable threads;
    @observable threadViewModels;
    @observable loading;

    unsubscribe = thread => {
        this.loading = true;
        return new Promise((resolve, reject) => {
            this.props.showDialog(
                new DeleteDialog({
                    message: new IntlMessage({
                        message: defineMessages({ ARE_YOU_SURE: { id: 'ARE_YOU_SURE' } }).ARE_YOU_SURE,
                        values: { gender: 'm' },
                    }),
                    label: new IntlMessage({
                        message: defineMessages({ DISABLE_NOTIFICATIONS: { id: 'DISABLE_NOTIFICATIONS' } })
                            .DISABLE_NOTIFICATIONS,
                    }),
                    del: () =>
                        thread
                            .toggleNotifications()
                            .then(() => {
                                let page;
                                if (this.props.threadsTableViewModel.threads.results.length == 1) {
                                    page = this.props.threadsTableViewModel.threads.page - 1 || 1;
                                    this.props.history.navigate({
                                        pathname: this.props.location.pathname,
                                        search: `?page=${page}`,
                                    });
                                    return;
                                }
                                page = this.props.threadsTableViewModel.threads.page;
                                return thread.delegate
                                    .loadSubscribedThreads({ page })
                                    .then(threads => this.props.threadsTableViewModel.setThreads(threads));
                            })
                            .finally(() => {
                                this.loading = false;
                                resolve();
                            }),
                    cancel: () => {
                        this.loading = false;
                        resolve();
                    },
                })
            );
        });
    };

    render() {
        const { styles, threadsTableViewModel } = this.props;
        const threads = threadsTableViewModel?.threads;

        return (
            <div className={styles.threadsTable}>
                <table className={styles.table}>
                    <thead className={styles.heading}>
                        <tr className={styles.headingRow}>
                            <th className={styles.headingLabel}>
                                <FormattedMessage id='TITLE' />
                            </th>
                            <th />
                        </tr>
                    </thead>
                    <tbody className={styles.body}>
                        {!threadsTableViewModel || threadsTableViewModel.loading
                            ? Array(5)
                                  .fill(null)
                                  .map((_, index) => <PlaceholderThreadRow borderTop={index !== 0} />)
                            : null}
                        {threadsTableViewModel?.threadViewModels &&
                            threadsTableViewModel.threadViewModels.map(threadViewModel => (
                                <tr key={threadViewModel.thread.id} className={styles.thread}>
                                    <td className={styles.title}>
                                        <LinkView link={threadViewModel.link} className={styles.link} />
                                    </td>
                                    <td>
                                        <ActionButtonView
                                            disabled={this.loading}
                                            onClick={() => this.unsubscribe(threadViewModel.thread)}
                                            icon={<SVGIcon style={{ image: styles.icon }} src={notification} />}
                                        >
                                            <FormattedMessage id='DISABLE_NOTIFICATIONS'>
                                                {msg => <span className={styles.oneline}>{msg}</span>}
                                            </FormattedMessage>
                                        </ActionButtonView>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
                {threadsTableViewModel?.threadViewModels?.length === 0 ? (
                    <div className={styles.noContent}>
                        <FormattedMessage id='FORUM_NO_THREADS' />
                    </div>
                ) : null}
                {threads?.pages && threads.pages > 1 ? (
                    <div className={styles.pager}>
                        <PagerView count={threads.pages} current={threads.page} />
                    </div>
                ) : null}
            </div>
        );
    }
}

export default withRouter(SubscribedTopicsView);
